/**
 * Os níveis que cada recurso oferece.
 *
 * Os rótulos são exatamente os textos que aparecem na matriz de
 * #v-acessos no protótipo. Editar a matriz (documento 19) é escolher
 * um destes níveis por célula — não marcar um checkbox.
 */
const negado = (rotulo = 'Não') => ({
    rotulo,
    acoes: [],
    escopo: 'nenhum',
    projecao: 'basico',
});
export const NIVEIS = {
    dashboard: {
        total: { rotulo: 'Total', acoes: ['ler'], escopo: 'todos', projecao: 'completo' },
        sem_margem_custos: {
            rotulo: 'Sem margem e custos',
            acoes: ['ler'],
            escopo: 'todos',
            projecao: 'sem_margem_custos',
        },
        nao: negado(),
    },
    alunos: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'criar', 'editar', 'excluir', 'anonimizar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        criar_editar: {
            rotulo: 'Criar e editar',
            acoes: ['ler', 'criar', 'editar'],
            escopo: 'todos',
            // AMBIGUIDADE conhecida: a observação médica é dado de saúde, a
            // categoria mais sensível da LGPD. O protótipo mostra a recepção
            // abrindo a ficha completa no log de auditoria de #v-acessos,
            // mas não diz se ela enxerga a lesão no joelho.
            // Padrão adotado: NÃO enxerga. Trocar para 'completo' é uma
            // palavra, e o mecanismo suporta as duas leituras.
            projecao: 'sem_saude',
        },
        proprias_turmas: {
            rotulo: 'Alunos das próprias turmas',
            acoes: ['ler'],
            escopo: 'proprias_turmas',
            projecao: 'sem_saude',
        },
        proprios_dados: {
            rotulo: 'Os próprios dados',
            acoes: ['ler', 'editar'],
            escopo: 'proprio_registro',
            projecao: 'completo',
        },
        nao: negado(),
    },
    perfis: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'criar', 'editar', 'excluir'],
            escopo: 'todos',
            projecao: 'completo',
        },
        nao: negado(),
    },
    turmas: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'criar', 'editar', 'excluir', 'registrar_chamada'],
            escopo: 'todos',
            projecao: 'completo',
        },
        criar_editar: {
            rotulo: 'Criar e editar',
            acoes: ['ler', 'criar', 'editar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        propria_grade_chamada: {
            rotulo: 'Própria grade e chamada',
            acoes: ['ler', 'registrar_chamada'],
            escopo: 'propria_grade',
            projecao: 'completo',
        },
        propria_turma: {
            rotulo: 'Própria turma',
            acoes: ['ler'],
            escopo: 'propria_matricula',
            projecao: 'basico',
        },
        nao: negado(),
    },
    planos: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'criar', 'editar', 'excluir', 'aplicar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        ver_aplicar: {
            rotulo: 'Ver e aplicar',
            acoes: ['ler', 'aplicar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        proprio_plano: {
            rotulo: 'Próprio plano',
            acoes: ['ler'],
            escopo: 'proprio_registro',
            projecao: 'basico',
        },
        nao: negado(),
    },
    cobrancas: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'criar', 'editar', 'cancelar', 'registrar_pagamento'],
            escopo: 'todos',
            projecao: 'completo',
        },
        registrar_pagamento: {
            rotulo: 'Registrar pagamento',
            acoes: ['ler', 'registrar_pagamento'],
            escopo: 'todos',
            projecao: 'completo',
        },
        propria_fatura: {
            rotulo: 'Própria fatura',
            acoes: ['ler'],
            escopo: 'proprio_registro',
            projecao: 'basico',
        },
        nao: negado(),
    },
    agenda: {
        total: {
            rotulo: 'Total',
            // `solicitar` também, embora o Dono marque direto em vez de pedir:
            // "Total" é o topo da ordem de alcance do documento 19
            // (alcance.ts) — todo nível do recurso cabe nele. Sem a ação aqui,
            // o nível do Aluno deixaria de caber no Total, e trocar o Dono de
            // perfil para o Aluno contaria como ELEVAÇÃO.
            acoes: ['ler', 'criar', 'editar', 'cancelar', 'excluir', 'solicitar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        criar_editar_cancelar: {
            rotulo: 'Criar, editar, cancelar',
            acoes: ['ler', 'criar', 'editar', 'cancelar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        visualizar: {
            rotulo: 'Visualizar',
            acoes: ['ler'],
            escopo: 'todos',
            // O professor vê a grade, não o valor nem a forma de pagamento
            // da locação avulsa.
            projecao: 'basico',
        },
        horarios_livres_solicitacao: {
            rotulo: 'Horários livres e solicitação',
            acoes: ['ler', 'solicitar'],
            escopo: 'horarios_livres',
            projecao: 'basico',
        },
        nao: negado(),
    },
    bi: {
        total: { rotulo: 'Total', acoes: ['ler'], escopo: 'todos', projecao: 'completo' },
        nao: negado(),
    },
    financeiro: {
        total: {
            rotulo: 'Total',
            // `cancelar` é a previsão que não vai acontecer — "Só a PREVISÃO
            // se cancela: o fato se estorna" (11-financeiro-razao.md). Estorno
            // e correção usam `editar`: são lançamentos novos ou ajuste de um
            // existente, não uma quarta ação distinta na matriz.
            acoes: ['ler', 'criar', 'editar', 'excluir', 'cancelar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        lancar_sem_folha: {
            rotulo: 'Lançar, sem ver folha',
            acoes: ['ler', 'criar', 'editar', 'cancelar'],
            // Duas dimensões ao mesmo tempo: a linha de folha não aparece
            // (escopo) E o detalhamento por professor não é selecionado
            // (projeção). Ver 11-financeiro-razao.md — é o caso canônico.
            escopo: 'sem_sensivel',
            projecao: 'sem_folha',
        },
        nao: negado(),
    },
    folha: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'criar', 'editar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        proprio_pagamento: {
            rotulo: 'Próprio pagamento',
            acoes: ['ler'],
            escopo: 'proprio_registro',
            projecao: 'basico',
        },
        nao: negado(),
    },
    estoque: {
        total: {
            rotulo: 'Total',
            // `estornar` é só do Dono por padrão: quem recebe em dinheiro e estorna
            // faria a venda sumir. A matriz editável permite delegar.
            acoes: ['ler', 'criar', 'editar', 'excluir', 'estornar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        // É o nível da Gestão e do atalho "Atendente do bar" — ver o
        // registro de decisão em matriz.ts. Sem `excluir`, de propósito.
        sem_margem_custos: {
            rotulo: 'Sem margem e custos',
            acoes: ['ler', 'criar', 'editar'],
            escopo: 'todos',
            projecao: 'sem_margem_custos',
        },
        nao: negado(),
    },
    comanda: {
        total: {
            rotulo: 'Total',
            acoes: ['ler', 'abrir', 'lancar', 'fechar', 'cancelar', 'estornar'],
            escopo: 'todos',
            projecao: 'completo',
        },
        abrir_lancar_fechar: {
            rotulo: 'Abrir, lançar, fechar',
            acoes: ['ler', 'abrir', 'lancar', 'fechar'],
            escopo: 'todos',
            // Fecha a venda sem ver o custo congelado nos itens: a autoria
            // continua vindo, o `custoUnitario` não. Mudar esta projeção exige
            // a variante correspondente em `comandaSelect` (estoque.projection.ts).
            projecao: 'sem_margem_custos',
        },
        abrir_lancar: {
            rotulo: 'Abrir e lançar',
            acoes: ['ler', 'abrir', 'lancar'],
            escopo: 'todos',
            projecao: 'basico',
        },
        propria_comanda: {
            rotulo: 'Própria comanda',
            acoes: ['ler'],
            escopo: 'propria_comanda',
            projecao: 'basico',
        },
        nao: negado(),
    },
};
//# sourceMappingURL=niveis.js.map